import { useCallback, useEffect, useRef } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  checkpointToShapeData,
  readShapeTextFrameUidsFromCheckpointMetadata,
} from '@/lib/checkpoint/checkpoint-to-shapes'
import { checkpointToLinkedTextElements } from '@/lib/checkpoint/checkpoint-to-linked-text'
import {
  createUidAllocator,
  readMaxUidFromCheckpointMetadata,
} from '@/lib/text-linking/text-frame-uid'
import {
  chainContaining,
  chainsContainingAny,
  fromWireTextChains,
  hasOutgoingLink,
  maxUidInChains,
  removeBoxFromChains,
  sanitizeChains,
  toWireTextChains,
} from '@/lib/text-linking/text-chain-mutator'
import { type StorySlice, changedSlices } from '@/lib/text-linking/chain-story'
import { canEditChainFrameContent } from '@/lib/text-linking/chain-content-edit-policy'
import type { ChainFrameFit } from '@/lib/text/types/chain-frame-fit'
import type { LinkableTextFrame } from '@/lib/text-linking/types/linkable-text-frame'
import { collectLinkableFrames, isShapeBackedFrame } from '@/lib/text-linking/linkable-text-frame'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import type { TextRunStyleUpdate } from '@/lib/text/types/text-run-style-update'
import { applyStyleUpdateToParagraphs } from '@/lib/text/text-style-engine'
import type { Paragraph, TextChain } from '@/api/types/stowed-element'
import type { ShapeTextBakeContext } from '@/lib/elements/stowed-element-builder'
import type { ChainStoryFrame } from '../../types/chain-story-frame'
import type { ChainStyleApplyResult } from '../../types/chain-style-apply-result'
import { NOT_HANDLED_CHAIN_STYLE } from '../../constants/chain-style'
import { buildStowedShapeElement } from '../../helpers/build-stowed-shape-element'
import { paragraphsForWriteBack } from '../../helpers/paragraphs-for-write-back'
import { chainFrameFit } from '../../text-chain/chain-frame-fit'
import { distributeChainStory } from '../../text-chain/distribute-chain-story'
import type {
  HomeDocumentLayout,
  HomeFoundation,
  HomeHistory,
  HomePersistence,
  HomeZoomAndSpread,
} from '../../types/home-groups'

/**
 * Linked text chains: the render-time bake context, frame uid minting and chain hydration from the
 * checkpoint, the story engine (story frames, slices, rider persistence), live projection,
 * redistribution, repair after history steps and frame deletes, and chain-wide text style apply.
 */
export function useHomeTextChains(
  deps: Pick<
    HomeFoundation,
    | 'textChains'
    | 'setTextChains'
    | 'bumpTextPanelRevision'
    | 'shapeManagerRef'
    | 'shapeTextBakeRef'
    | 'fontLookupRef'
    | 'textChainsRef'
    | 'deleteGestureScopeRef'
    | 'chainStoryRedistributedRef'
    | 'chainSeamMemoRef'
    | 'chainProjectionBaselineRef'
    | 'applyTextStyleAcrossChainRef'
  > &
    Pick<HomeZoomAndSpread, 'data' | 'spreadId'> &
    Pick<HomeDocumentLayout, 'spreadProjection'> &
    Pick<HomePersistence, 'pushAction' | 'enqueueUpdate' | 'markEdited'> &
    Pick<HomeHistory, 'repairChainsAfterHistoryStepRef'>
) {
  const {
    textChains,
    setTextChains,
    bumpTextPanelRevision,
    shapeManagerRef,
    shapeTextBakeRef,
    fontLookupRef,
    textChainsRef,
    deleteGestureScopeRef,
    chainStoryRedistributedRef,
    chainSeamMemoRef,
    chainProjectionBaselineRef,
    applyTextStyleAcrossChainRef,
    data,
    spreadId,
    spreadProjection,
    pushAction,
    enqueueUpdate,
    markEdited,
    repairChainsAfterHistoryStepRef,
  } = deps

  // Assigned during render, like `textChainsRef` above: a save can fire mid-gesture and must bake
  // against the wrap context and chain state of the render it is saving, not the previous one.
  //
  // `wrap` is built from `getShapes()` (synchronously fresh — see `buildChainStoryFrames`'s
  // identical fix) rather than the memoized `getShapeTextWrapContext()`, which is keyed on React's
  // `shapes` state and only updates on ShapeManager's own next render. A wrap-source's own commit
  // persists its affected chain members in this SAME synchronous handler (`chainsAffectedByWrapSourceChange`
  // + `redistributeChainStory`), so baking against the memoized context here would bake the WIRE —
  // and therefore any PDF built from it — against the obstacle's pre-commit position, even though
  // the paragraphs were correctly redistributed against its new one (WMPCM linked-text-wrap + PDF
  // investigation).
  shapeTextBakeRef.current = (shape: ShapeData): ShapeTextBakeContext => {
    const smgr = shapeManagerRef.current
    const freshShapesForBake = smgr?.getShapes() ?? []
    const hasActiveWrapSourceForBake = freshShapesForBake.some(
      (s) => s.wrapStyle != null && s.wrapStyle !== 'none' && s.hidden !== true
    )

    return {
      wrap: hasActiveWrapSourceForBake
        ? {
            allShapes: freshShapesForBake,
            zIndexOf: smgr?.getShapeTextWrapContext()?.zIndexOf ?? (() => undefined),
          }
        : undefined,
      hasNextInChain:
        shape.uid != null && hasOutgoingLink(textChainsRef.current, String(shape.uid)),
    }
  }

  // Seeded with a REAL allocator from the start (not a `() => 0` stub) so a frame linked before
  // the checkpoint metadata effect below has run — a slow network, or the metadata request
  // failing outright — still gets a distinct uid instead of the same constant every call. Two
  // frames linked in the same gesture (`ensureFrameUid` for the source, `nextTextFrameUidRef` for
  // a freshly-created target) each call this once, synchronously; a stub that always answers "0"
  // silently merged them into a single-member chain (`sanitizeChains`/`resolveChainsToFrames` can
  // only keep one live frame per uid), which is exactly what read as "linking does nothing" and
  // "the new frame links backwards" — there was no real second frame in the chain to flow into.
  const nextTextFrameUidRef = useRef<() => number>(createUidAllocator(0))
  // High-water mark of every uid actually handed out via `mintTextFrameUid`, independent of which
  // allocator closure produced it. Without this, the metadata effect below could install a fresh
  // `createUidAllocator` reseeded from the checkpoint's own max — lower than a uid this session's
  // fallback allocator already minted and assigned to a live shape — and duplicate it right back.
  const mintedTextFrameUidHighWaterRef = useRef(0)

  const mintTextFrameUid = useCallback((): number => {
    const uid = nextTextFrameUidRef.current()

    if (uid > mintedTextFrameUidHighWaterRef.current) mintedTextFrameUidHighWaterRef.current = uid

    return uid
  }, [])

  /**
   * Marks every uid up to `uid` as taken. A placed template arrives with uids the backend already
   * renumbered above the highest one we told it about (the seed came from `mintTextFrameUid`), so
   * the allocator has to move past them or the next new text frame would reuse one and silently
   * join the template's chain.
   */
  const reserveTextFrameUidsThrough = useCallback((uid: number): void => {
    if (uid <= mintedTextFrameUidHighWaterRef.current) return

    mintedTextFrameUidHighWaterRef.current = uid
    nextTextFrameUidRef.current = createUidAllocator(uid)
  }, [])

  useEffect(() => {
    setTextChains([])
  }, [spreadId, setTextChains])

  useEffect(() => {
    if (!data?.metadata) return
    // Every chain-eligible text frame is a ShapeManager shape now (text-unification Phase 3) — see
    // `readShapeTextFrameUidsFromCheckpointMetadata` for the full account (WMPCM-9070).
    const validUids = readShapeTextFrameUidsFromCheckpointMetadata(data.metadata)
    // The server's `linkedTextElements.textChain[].textBox[].uid` now carries each member's
    // objectId, not this session's uid scheme — translate back via `fromWireTextChains` BEFORE
    // anything below reads `.uid` as a session uid. `checkpointToShapeData` is a pure parse of this
    // same `metadata`, independent of whether shapes have reached the manager yet, so this cannot
    // race the manager's own load. Untranslated, `sanitizeChains` would find no member's (objectId)
    // uid inside `validUids` (a set of session uids) and silently drop every chain on load.
    const rawChains = checkpointToLinkedTextElements(data.metadata)?.textChain ?? []
    const chains = fromWireTextChains(rawChains, checkpointToShapeData(data.metadata))
    // Extracted rather than inlined into `sanitizeChains` so the allocator below can be seeded from
    // the translated list too — see the comment there (WMPCM-7036).

    setTextChains(sanitizeChains(chains, validUids))
    // Seeded from the translated chain list as well as the loaded elements:
    // `readMaxUidFromCheckpointMetadata` scans elements only, so a uid freed by a deleted frame is
    // immediately reissuable, and any chain still naming it would silently adopt the next frame that
    // gets it (WMPCM-7036). Reading before `sanitizeChains` is deliberate — the uids it drops are
    // precisely the ones to keep reserved. `maxUidInChains` needs the TRANSLATED list — the raw one's
    // `.uid` values are objectId strings, which `Number(...)` never parses as the uid they used to
    // be, so scanning the raw list would silently reserve nothing. Also floored at
    // `mintedTextFrameUidHighWaterRef` — see that ref's own comment.
    nextTextFrameUidRef.current = createUidAllocator(
      Math.max(
        readMaxUidFromCheckpointMetadata(data.metadata),
        maxUidInChains(chains),
        mintedTextFrameUidHighWaterRef.current
      )
    )
  }, [data?.metadata, setTextChains])

  /**
   * Builds the window a distribution runs over — one `StoryFrame` per live chain member, in CHAIN
   * order, read from the manager.
   *
   * Head-first, never anchor-first. Under the story model every trigger re-splits the whole story
   * from the head (D4), which is what makes "grow frame 1 and the text flows back up" fall out of the
   * model rather than being a feature behind a flag. Head-scoping is also a fixed point once the
   * upstream frames are full, so the extra work is a no-op in the common case and `changedSlices`
   * filters the write-back down to what actually moved.
   *
   * `members` is therefore a chain's whole `textBox`, or a contiguous run of it for the two gestures
   * that split one story into two (link, unlink).
   *
   * `excludeFrameIds` names frames the caller's own gesture is removing, and it is MORE load-bearing
   * than it was under the old anchor-scoped reflow, not less: redistribution is now unconditional, so
   * a doomed frame left in the window absorbs a survivor's text moments before it disappears — and on
   * the shape path that text is then lost outright, because `deleteSelectedShape` iterates the React
   * `shapes` array while these write-backs only advance `shapesRef`.
   */
  const buildChainStoryFrames = useCallback(
    (
      members: readonly { uid: string }[],
      excludeFrameIds?: ReadonlySet<string>
    ): ChainStoryFrame[] => {
      const smgr = shapeManagerRef.current
      const byUid = new Map(
        collectLinkableFrames(smgr?.getShapes() ?? [])
          .filter((f) => f.uid != null)
          .map((f) => [String(f.uid), f])
      )
      const fonts = fontLookupRef.current ?? undefined
      // The wrap SOURCES' own geometry must be read fresh here, not from the renderer's memoized
      // `getShapeTextWrapContext()` — that memo is keyed on React's `shapes` state and only
      // updates on ShapeManager's own next render, so a chain reflow triggered synchronously by
      // the wrap source's OWN just-committed move/resize/style change (see
      // `chainsAffectedByWrapSourceChange`) would otherwise still measure against its PRE-commit
      // position for up to one render — silently baking a stale (unwrapped, or wrongly-wrapped)
      // layout into the persisted wire, and therefore into any PDF built from it, even though the
      // very next paint corrects the on-screen render (WMPCM linked-text-wrap + PDF investigation).
      // `smgr?.getShapes()` is synchronously fresh — `updateShape` advances it immediately, see its
      // own comment in shape-manager/hooks/use-shape-updates.ts — so building the context from that instead closes the
      // gap. Falling back to the memoized context's own `zIndexOf` is safe: unified layer order
      // never changes as a side effect of a geometry/style commit, so it cannot be stale in the
      // same way.
      const freshShapesForWrap = smgr?.getShapes() ?? []
      const hasActiveWrapSource = freshShapesForWrap.some(
        (s) => s.wrapStyle != null && s.wrapStyle !== 'none' && s.hidden !== true
      )
      const wrap: ShapeTextWrapContext | undefined = hasActiveWrapSource
        ? {
            allShapes: freshShapesForWrap,
            zIndexOf: smgr?.getShapeTextWrapContext()?.zIndexOf ?? (() => undefined),
          }
        : undefined

      return members
        .map((tb) => byUid.get(tb.uid))
        .filter((f): f is LinkableTextFrame => f != null && !excludeFrameIds?.has(f.id))
        .map((f) => ({
          id: f.id,
          paragraphs: f.paragraphs,
          fit: chainFrameFit(f, fonts, wrap),
          frame: f,
        }))
    },
    [fontLookupRef, shapeManagerRef]
  )

  /**
   * Writes a distribution's slices into the manager and pushes one history action per frame.
   *
   * `bypassEditGuards` because a locked or frozen chain member still receives flowed text
   * (WMPCM-6972). Frozen alone would pass regardless — `paragraphs` is not placement geometry — but
   * `shouldBlockShapeUpdate` rejects *every* update on a LOCKED shape, so without it a locked member
   * silently drops its share of the flow (WMPCM-7052 AC3). Flowed text is not a user content edit; it
   * is written into protected members deliberately, and the alternative is losing it.
   *
   * The actively-edited frame is written like any other member (WMPCM-9199). It used to be excluded
   * on the reasoning that its content belonged to the live editor, which left it holding text that had
   * already flowed downstream — so every later commit re-evicted the same text and the next frame
   * gained one duplicate per debounce window, undo unable to clean them up. `ShapeTextEditor`'s
   * trim-resync effect re-seeds its live buffer from the written model instead.
   *
   * Every push shares `historyTxnId`, and `use-undo-redo.ts` collapses contiguous same-txn actions
   * into one `compound` — so N frame updates already undo as a single step. Do not invent a
   * story-level undo action type.
   *
   * `reflowOnly` tags those pushes for `redistributeChainStory`, so a compound undo step selects the
   * edit that caused the reflow rather than every frame it touched (WMPCM-10558).
   *
   * `skipHistoryPush` (WMPCM-9199) is for exactly one caller: `runHistoryStep`'s post-undo/redo
   * chain repair. That pass re-derives a chain AFTER an undo/redo step already applied, to fix a
   * chain a replayed snapshot left inconsistent — it must write the correction without pushing a
   * new undoable action, or undoing once would leave a second, unrelated entry on the stack for
   * the user to undo again. Every other caller must keep pushing history as before.
   */
  const applyStorySlices = useCallback(
    (
      frames: readonly ChainStoryFrame[],
      slices: readonly StorySlice[],
      historyTxnId: string,
      options?: { skipHistoryPush?: boolean; reflowOnly?: boolean }
    ) => {
      const smgr = shapeManagerRef.current
      const byId = new Map(frames.map((f) => [f.id, f.frame]))

      for (const slice of slices) {
        const frame = byId.get(slice.id)

        if (!frame || !isShapeBackedFrame(frame)) continue

        const shapeParagraphs = paragraphsForWriteBack(slice.paragraphs)

        smgr?.updateShape(frame.id, { paragraphs: shapeParagraphs }, { bypassEditGuards: true })
        if (!options?.skipHistoryPush) {
          pushAction({
            type: 'update',
            shapeId: frame.id,
            before: { paragraphs: frame.paragraphs },
            after: { paragraphs: shapeParagraphs },
            historyTxnId,
            ...(options?.reflowOnly ? { reflowOnly: true } : {}),
          })
        }
      }
    },
    [pushAction, shapeManagerRef]
  )

  /**
   * Persists the chain-topology rider, plus every frame the caller names.
   *
   * The frame matching `riderFrameId` carries the `linkedTextElements` rider so the chain list is
   * saved in the same request. That list is a spread-level sidecar the API accepts only riding on an
   * element add or update, so there is no chain-only write to fall back on.
   *
   * **Extracted from the distribution rather than living inside it, and that is load-bearing.**
   * `handleCreateLinkedTextFrame`'s only chain-rider persist is this enqueue — its own `enqueueAdd`
   * carries no rider, and ChangeQueue coalesces the add and this update on one objectId while keeping
   * the `linkedTextElements`. A link path that stops distributing without replacing this enqueue
   * makes the link silently vanish on reload.
   *
   * Reads each frame from the live manager rather than from a pre-write snapshot: `updateShape`
   * advances `shapesRef` synchronously (see its comment in `shape-manager/hooks/use-shape-updates.ts`), so by the time this
   * runs the manager already holds the distributed paragraphs.
   */
  const persistChainRider = useCallback(
    (
      chainsAfter: TextChain[],
      riderFrameId: string,
      historyTxnId: string,
      persistFrameIds?: ReadonlySet<string>
    ) => {
      const shapes = shapeManagerRef.current?.getShapes() ?? []
      const fonts = fontLookupRef.current ?? undefined
      const ids = new Set<string>(persistFrameIds)
      // The wire speaks objectId, not the client-minted uid every internal chain lookup uses —
      // translate once, right at the payload boundary. Per backend (Preethi's report): the
      // `linkedTextElements` payload must carry each member's objectId, not its uid. See
      // `toWireTextChains`.
      const wireTextChain = toWireTextChains(chainsAfter, shapes)

      ids.add(riderFrameId)

      for (const id of ids) {
        const shape = shapes.find((s) => s.id === id)

        if (!shape) continue

        enqueueUpdate(
          buildStowedShapeElement(
            shapeTextBakeRef.current,
            shape,
            undefined,
            spreadProjection,
            fonts
          ),
          {
            operation: 'update' as const,
            ...(id === riderFrameId ? { linkedTextElements: { textChain: wireTextChain } } : {}),
            meta: { historyTxnId },
          }
        )
      }
    },
    [enqueueUpdate, spreadProjection, fontLookupRef, shapeManagerRef, shapeTextBakeRef]
  )

  /**
   * Flows the chain on **every keystroke**, so the next frame shows the letters that no longer fit
   * the one being typed in at the moment they stop fitting — not 500 ms later.
   *
   * Two paths used to move that text and neither covered the plain case. The debounced shape-text
   * commit runs `redistributeChainStory`, but only once typing pauses. `ShapeTextEditor`'s hand-off
   * effect flushes that commit immediately — and only when the caret itself has gone past capacity,
   * because its job is to follow the caret across the boundary. Type into the MIDDLE of a full frame
   * and the trailing characters cross the seam while the caret does not, so nothing fired and the
   * downstream frame sat stale for the whole debounce window.
   *
   * **Display only.** No history action, no `markEdited`, no enqueue: undo granularity and write
   * amplification both stay exactly where the debounce put them. The frames it writes are recorded in
   * `chainProjectionBaselineRef` first so the commit that follows can still report the right `before`
   * and still enqueue the frames this pass moved text into.
   *
   * It is deliberately the SAME distribution as the commit — same window, same head-first
   * `distributeStory`, same seam memo — rather than a cheaper "just push the overset onward"
   * approximation. Anything the projection decides differently from the commit is text written twice
   * or dropped at the seam (WMPCM-9199's duplicate-per-debounce-window bug, from the other
   * direction).
   *
   * The seam memo is recorded, not just read, and that is load-bearing rather than housekeeping. This
   * pass rewrites the downstream frame's text, which invalidates the entry keyed on its
   * `downstreamKey`; the next assembly would find no entry and fall back to `seamWasCharacterSplit`,
   * which is provably blind to an over-capacity upstream frame — i.e. to every in-session edit (see
   * `ChainSeamMemo`). A mid-paragraph overflow would then arrive downstream as a new paragraph
   * instead of joining the line it belongs to.
   *
   * The edited frame is trimmed like any other member. Excluding it is the tempting move — it owns
   * the live buffer, and `ShapeTextEditor`'s trim-resync then has nothing to follow — but it leaves
   * the frame holding text that now also lives downstream, so the very next keystroke assembles a
   * story with those characters in it twice (WMPCM-9199). The resync exists for exactly this.
   *
   * Returns whether it wrote anything, which the caller needs: a projection can flow ALL of the typed
   * characters onward and leave the edited frame's paragraphs byte-identical to its edit-start
   * baseline, and `commitShapeTextNow`'s baseline diff would then early-out and commit neither the
   * history nor the persist for the frame that received them.
   */
  const projectChainStoryLive = useCallback(
    (shapeId: string): boolean => {
      const chains = textChainsRef.current
      // Read straight off the shape rather than through `collectLinkableFrames`: this runs on every
      // keystroke, and the only thing needed to reject a non-member is its uid. `handleShapeTextParagraphsChange`
      // only fires for a frame in text edit, so there is no "is this linkable" question to answer.
      const uid = shapeManagerRef.current?.getShapes().find((s) => s.id === shapeId)?.uid

      if (uid == null) return false

      // Early-out before any fitting: an ordinary text frame is the overwhelmingly common case and
      // must not pay a chain-wide re-fit per keystroke.
      const chain = chainContaining(chains, String(uid))

      if (!chain) return false

      const frames = buildChainStoryFrames(chain.textBox)

      if (frames.length < 2) return false

      const baselines = chainProjectionBaselineRef.current
      const slices = distributeChainStory(chainSeamMemoRef.current, frames)

      const changed = changedSlices(frames, slices)

      if (changed.length === 0) return false

      const byId = new Map(frames.map((f) => [f.id, f.frame]))

      for (const slice of changed) {
        const frame = byId.get(slice.id)

        if (!frame || !isShapeBackedFrame(frame)) continue

        // Every frame EXCEPT the one being edited. Its keystroke has already landed in the model by
        // the time this runs, so recording it here would baseline the post-keystroke content and the
        // commit would then push a history action whose `before` still contains the typed character —
        // one Ctrl+Z would move that character back into this frame's overset instead of deleting it.
        // The manager owns this frame's baseline (`editStartShapeTextParagraphsRef`) and its commit
        // already pushes the right action from it; `redistributeChainStory` compares this frame
        // against the live model and so stays out of the way.
        //
        // First projection of the window wins for the others: the baseline has to predate every
        // projection, not just the most recent one.
        if (frame.id !== shapeId && !baselines.has(frame.id)) {
          baselines.set(frame.id, frame.paragraphs)
        }

        // `bypassEditGuards` for the same reason `applyStorySlices` needs it — flowed text is written
        // into locked and frozen members deliberately (WMPCM-6972 / WMPCM-7052 AC3).
        shapeManagerRef.current?.updateShape(
          frame.id,
          { paragraphs: paragraphsForWriteBack(slice.paragraphs) },
          { bypassEditGuards: true }
        )
      }

      return true
    },
    [
      buildChainStoryFrames,
      chainProjectionBaselineRef,
      chainSeamMemoRef,
      shapeManagerRef,
      textChainsRef,
    ]
  )

  /**
   * The live-drag counterpart to `projectChainStoryLive` above: instead of the one chain containing
   * a frame being typed into, this re-derives EVERY chain's story and writes back whatever changed.
   * Fired from `onLiveShapeGeometryChangeForWrap` (RAF-throttled to at most once per animation
   * frame — see `scheduleLiveWrapSync` in shape-manager/hooks/use-shape-manager-state.ts), so a wrap-source obstacle being
   * dragged over a chain member reflows the chain WHILE the drag is still in progress, matching the
   * user's request to see the reflow live rather than only once the drag commits.
   *
   * Same display-only contract as `projectChainStoryLive`: no history, no persist, no `markEdited`.
   * The commit that follows re-derives and persists for real (via `chainsAffectedByWrapSourceChange`
   * + `redistributeChainStory`); this exists purely so the on-screen text keeps up with the pointer
   * instead of jumping once at commit.
   */
  const projectAllChainsLive = useCallback(() => {
    const chains = textChainsRef.current

    for (const chain of chains) {
      const frames = buildChainStoryFrames(chain.textBox)

      if (frames.length < 2) continue

      const baselines = chainProjectionBaselineRef.current
      const slices = distributeChainStory(chainSeamMemoRef.current, frames)

      const changed = changedSlices(frames, slices)

      if (changed.length === 0) continue

      const byId = new Map(frames.map((f) => [f.id, f.frame]))

      for (const slice of changed) {
        const frame = byId.get(slice.id)

        if (!frame || !isShapeBackedFrame(frame)) continue

        // Same "record the pre-projection baseline once" rule as `projectChainStoryLive` — first
        // projection of the window wins, so the eventual commit's `before` predates every live
        // write this gesture made, not just the most recent one.
        if (!baselines.has(frame.id)) {
          baselines.set(frame.id, frame.paragraphs)
        }

        shapeManagerRef.current?.updateShape(
          frame.id,
          { paragraphs: paragraphsForWriteBack(slice.paragraphs) },
          { bypassEditGuards: true }
        )
      }
    }
  }, [
    buildChainStoryFrames,
    chainProjectionBaselineRef,
    chainSeamMemoRef,
    shapeManagerRef,
    textChainsRef,
  ])

  /**
   * Re-splits the whole story of the chain containing `chainMemberUid` across every one of its
   * frames, head to tail, applies what changed, and persists it with the chain rider.
   *
   * **This is every steady-state trigger**: typing, delete, cut, resize, a font-size or leading
   * change, a chain-wide style apply. There are no options describing what kind of edit provoked it,
   * because under the story model none of them change the answer — `distributeStory` fills each frame
   * to capacity from the head, so a frame that grew, or type that shrank, pulls text back up simply
   * because the split is recomputed. The seven provenance heuristics the deleted `reflowChainOnce` needed
   * (`separateStoryAfterFrameId`, `allowBackwardPull`, `seamContinuesAfterFrameId`,
   * `preEditRiderParagraphs`, …) existed to reconstruct facts the story makes unnecessary.
   *
   * The two gestures that split ONE story into TWO — link and unlink — cannot use this and go through
   * `distributeAcrossChainBoundary` inside their own handlers instead.
   *
   * `chainMemberUid` only locates the chain; it does NOT scope the window. Index 0 of the window is
   * the chain head by construction, which is what removed the old `hasPrevInChain` field a caller had
   * to supply and could get wrong (WMPCM-9070 Bugs 1/7).
   *
   * The story is derived FRESH on every call and never cached. `applyHistoryAction` itself replays
   * recorded paragraphs without re-reflowing, and `linkedTextChainsUpsert` writes `textChainsRef`
   * directly, so a held story would go stale behind an undo and the next edit would redistribute from
   * text the frames no longer hold — the most likely silent text-loss path in this refactor.
   * `runHistoryStep` calls back in here (with `skipHistoryPush: true`) right after every undo/redo
   * step to repair exactly that gap — see its own comment (WMPCM-9199: a replayed compound whose
   * per-frame snapshots weren't mutually consistent otherwise left the chain corrupted, with no
   * further undo able to fix it).
   */
  const redistributeChainStory = useCallback(
    (
      chainsAfter: TextChain[],
      chainMemberUid: string,
      riderFrameId: string,
      historyTxnId: string,
      options?: {
        forcePersistFrameIds?: ReadonlySet<string>
        excludeFrameIds?: ReadonlySet<string>
        /** See `applyStorySlices`'s doc comment — only `runHistoryStep`'s post-undo/redo chain
         *  repair should ever pass this. */
        skipHistoryPush?: boolean
      }
    ) => {
      const chain = chainContaining(chainsAfter, chainMemberUid)

      if (!chain) return

      const frames = buildChainStoryFrames(chain.textBox, options?.excludeFrameIds)

      if (frames.length === 0) return

      const slices = distributeChainStory(chainSeamMemoRef.current, frames)

      // The window as the last COMMIT left it, which is NOT what the frames currently hold: the live
      // projection has already written some of them this edit window, without history and without
      // enqueueing anything. Comparing against the live model would report those frames unchanged
      // and drop them from both `applyStorySlices` and `persistChainRider` — the flowed text would
      // exist only in memory. See `chainProjectionBaselineRef`.
      const baselines = chainProjectionBaselineRef.current
      const committedFrames = frames.map((f) => {
        const baseline = baselines.get(f.id)

        return baseline
          ? { ...f, paragraphs: baseline, frame: { ...f.frame, paragraphs: baseline } }
          : f
      })

      const changed = changedSlices(committedFrames, slices)

      // Baseline-substituted, so each history action's `before` is the frame's committed content
      // rather than the projection's own intermediate write.
      // `reflowOnly`: whatever edit triggered this redistribution recorded its own action in the same
      // txn, so undoing that step selects what the user edited, not every frame the story touched.
      applyStorySlices(committedFrames, changed, historyTxnId, {
        skipHistoryPush: options?.skipHistoryPush,
        reflowOnly: true,
      })
      // Dropped as they are consumed, not at the end of the edit session: the next window's `before`
      // must be what this commit just wrote, not a baseline two windows deep.
      for (const f of frames) baselines.delete(f.id)

      const changedIds = changed.map((slice) => slice.id)

      // Scoped to the frames that actually changed, not to the window. `markEdited` acquires an
      // element lock, and head-scoping makes the window the whole chain on every single commit — so
      // an unscoped call would lock every member of a chain for the length of the edit window.
      if (changedIds.length > 0) void markEdited(changedIds)

      persistChainRider(
        chainsAfter,
        riderFrameId,
        historyTxnId,
        new Set([...changedIds, ...(options?.forcePersistFrameIds ?? [])])
      )
    },
    [
      applyStorySlices,
      buildChainStoryFrames,
      markEdited,
      persistChainRider,
      chainProjectionBaselineRef,
      chainSeamMemoRef,
    ]
  )

  /**
   * `runHistoryStep`'s post-undo/redo chain repair (WMPCM-9199) — see `repairChainsAfterHistoryStepRef`
   * and `redistributeChainStory`'s own doc comment for why this exists. For every shape id an
   * undo/redo step just touched (`outcome.persistence.shapeMarkEditedIds`), resolves its chain (if
   * any) via its current `uid` and re-derives that chain's story once, deduplicated so a compound
   * touching two members of the same chain doesn't reflow it twice. `skipHistoryPush: true` keeps
   * the correction off the undo/redo stack — it must never itself become an undoable action.
   *
   * Reads `uid` from the shape list AFTER the history step applied, not before: undoing an 'add'
   * removes the shape entirely, and undoing a 'remove' restores one — a stale before-step id list
   * would either look up a shape that's gone or miss one that just came back.
   */
  const repairChainsAfterHistoryStep = useCallback(
    (touchedShapeIds: readonly string[]) => {
      if (touchedShapeIds.length === 0) return

      const shapesAfterStep = shapeManagerRef.current?.getShapes() ?? []
      const shapesById = new Map(shapesAfterStep.map((shape) => [shape.id, shape]))
      const repairedChainIds = new Set<string>()

      for (const shapeId of touchedShapeIds) {
        const uid = shapesById.get(shapeId)?.uid

        if (uid == null) continue

        const chain = chainContaining(textChainsRef.current, String(uid))

        if (!chain || repairedChainIds.has(chain.id)) continue
        repairedChainIds.add(chain.id)

        redistributeChainStory(
          textChainsRef.current,
          String(uid),
          shapeId,
          `chain-undo-repair:${crypto.randomUUID()}`,
          { skipHistoryPush: true }
        )
      }
    },
    [redistributeChainStory, shapeManagerRef, textChainsRef]
  )

  // Refresh the ref target to the latest closure without re-binding it into `runHistoryStep`
  // itself — see `repairChainsAfterHistoryStepRef`'s own comment for why the indirection exists.
  useEffect(() => {
    repairChainsAfterHistoryStepRef.current = repairChainsAfterHistoryStep
  }, [repairChainsAfterHistoryStep, repairChainsAfterHistoryStepRef])

  /**
   * Repairs chain topology after a linked frame was deleted locally (WMPCM-6852/6860): splices the
   * deleted frame's neighbours together so the chain doesn't dangle on a uid that no longer exists,
   * then redistributes the chain's story — the dying frames' text included — across the survivors.
   *
   * The story is assembled from the chain as it stood BEFORE the splice, dying frames and all, and
   * then distributed over the survivors alone. That is what replaced the old "fold the deleted
   * frame's resident text into a neighbour, then reflow" two-step, and with it every question that
   * step had to answer by heuristic: which neighbour absorbs the text (the deleted
   * `findChainMergeTargetUid`), whether the boundary it lands on was a mid-paragraph split (the
   * deleted `mergeChainSegmentsInOrder`), and in
   * what order several victims' blocks concatenate (WMPCM-9070 Bug 2 — cutting a chain with a locked
   * member reordered the content). A story has no such questions: the victims' slices are simply part
   * of it, in chain order, and the survivors are re-filled from the head.
   *
   * Shared by the text-box and shape delete paths so deleting a converted shape frame behaves
   * identically to deleting a Text-tool frame (WMPCM-7988).
   *
   * Called once per deleted frame, and one gesture can delete several — three things make that safe:
   * `textChainsRef` (so each call sees the previous one's topology instead of a stale closure),
   * `deleteGestureScopeRef` (so no frame doomed later in the same gesture is treated as a survivor),
   * and `chainStoryRedistributedRef` (so the gesture's story is redistributed exactly once per
   * chain — see its declaration for why a second pass would double-count).
   */
  const repairChainAfterFrameDeleted = useCallback(
    (
      deleted: {
        id: string
        uid: number
        paragraphs: Paragraph[]
        /**
         * The deleted frame's own fitter, supplied by the caller because only it knows whether the
         * frame was a Text-tool box or a shape. Used, with `paragraphs`, to describe this frame in
         * the story window rather than re-deriving it from a manager entry that is on its way out.
         */
        fit: ChainFrameFit
        /**
         * The frame's own shape, passed in rather than looked up because the no-survivor branch
         * below has to serialize it *after* it is gone from the manager for the second and later
         * frames of a multi-frame gesture. `onShapeDeleted` fires before `removeShape`, so the
         * caller always has it.
         */
        shape: ShapeData
      },
      historyTxnId?: string
    ): void => {
      const deletedUid = String(deleted.uid)
      // Read the live mirror, never the `textChains` closure — see `textChainsRef`. Within one
      // multi-frame delete gesture this is what carries the previous repair's result forward.
      const chainsBefore = textChainsRef.current
      const deletedChain = chainContaining(chainsBefore, deletedUid)

      if (!deletedChain) return

      const smgr = shapeManagerRef.current
      const fonts = fontLookupRef.current ?? undefined
      // Every frame this gesture is removing, not just the one this call is for. The manager deletes
      // one element at a time, so a frame deleted later in the same gesture is still readable here
      // and would otherwise be treated as a live survivor — absorbing the story's tail, anchoring the
      // distribution, and getting a chain pointer persisted at it, all moments before it disappears.
      const gestureScope = deleteGestureScopeRef.current
      const inGestureScope = gestureScope != null && gestureScope.txnId === historyTxnId
      const coDeletedIds: ReadonlySet<string> = inGestureScope
        ? gestureScope.ids
        : new Set([deleted.id])
      // Identifies the delete/cut gesture whose repairs share one redistribution. A lone delete has
      // no scope of its own, so it keys on the frame's own id and can never batch with anything.
      const gestureKey = inGestureScope ? `txn:${historyTxnId}` : `frame:${deleted.id}`
      const dyingIds = new Set<string>([deleted.id, ...coDeletedIds])
      // Undo granularity: a delete that arrives with no transaction of its own still gets one, so the
      // N frame updates this redistribution pushes collapse into a single Ctrl+Z step.
      const storyTxnId = historyTxnId ?? `textlink-delete-reflow:${crypto.randomUUID()}`

      // The chain as it stood before this call's splice, dying frames INCLUDED — `onShapeDeleted`
      // fires before `removeShape`, so every victim of this gesture is still readable. That is what
      // makes the story complete: a deleted frame's resident text is part of it and gets
      // redistributed over the survivors rather than discarded (WMPCM-6860 AC1).
      //
      // The frame this call is for is described from the caller's own snapshot instead of the
      // manager's: that snapshot is what `onShapeDeleted` was handed, and its `fit` is the only one
      // that knows whether the frame was a Text-tool box or a shape without re-projecting it.
      const windowFrames = buildChainStoryFrames(deletedChain.textBox).map((f) =>
        f.id === deleted.id ? { ...f, paragraphs: deleted.paragraphs, fit: deleted.fit } : f
      )
      const survivorFrames = windowFrames.filter((f) => !dyingIds.has(f.id))
      const chainsAfter = removeBoxFromChains(chainsBefore, deletedUid)
      // Prefer a rider the payload can safely rewrite: `enqueueUpdate` carries a full
      // `buildStowedElement` of the rider's own state, not just the chain sidecar. A locked or frozen
      // frame is still accepted as a fallback rather than dropped — persisting the topology change
      // matters more than avoiding a re-write of an unchanged frame, and with a protected sole
      // survivor it is the only candidate there is.
      //
      // **Survivorship is the only test, and protection is not a reason to skip a frame.** A locked
      // or frozen survivor receives its share of the redistributed story like any other member
      // (WMPCM-7052 AC3): flowed text is not a user content edit, `applyStorySlices` writes it with
      // `bypassEditGuards` exactly as every other trigger does, and the alternative is silent text
      // loss. `canEditChainFrameContent` still governs the other chain paths in this file
      // (`applyTextStyleAcrossChain`, `handleChainBackspaceAtStart`) — this site simply is not one of
      // those edits.
      const chainRiderFrame =
        survivorFrames.find(
          (f) => canEditChainFrameContent(f.frame) && f.frame.frozen !== true && f.frame.uid != null
        ) ?? survivorFrames.find((f) => f.frame.uid != null)
      // Canvas-wide last resort, used only when no member of this chain survives at all: the topology
      // change still has to ride on something.
      const bystanders = collectLinkableFrames(smgr?.getShapes() ?? []).filter(
        (f) => !dyingIds.has(f.id) && f.uid != null
      )
      const bystanderRiderFrame =
        bystanders.find((f) => canEditChainFrameContent(f) && f.frozen !== true) ?? bystanders[0]

      // **One redistribution per chain per gesture.** The first call builds the story from every
      // frame of the chain and re-fills the survivors with all of it; by the time a second victim's
      // repair runs, the survivors already hold that text while the remaining victims still hold
      // their original slices, so re-assembling would count the same content twice. Keyed on the
      // CHAIN, never on the gesture txnId: one Ctrl+X can cut members of two independent chains
      // (`buildDeleteGestureScope` hashes the whole cuttable id set), and a txnId-keyed guard would
      // suppress the second chain's redistribution outright and lose its text.
      if (chainStoryRedistributedRef.current.gestureKey !== gestureKey) {
        chainStoryRedistributedRef.current = { gestureKey, chainIds: new Set() }
      }

      // Unconditional, and outside the redistribution block below: the topology changed whether or
      // not this call is the one that re-splits the story, and a baseline that outlives a topology
      // change pairs a pre-change `before` with a post-change slice. See
      // `chainProjectionBaselineRef`.
      chainProjectionBaselineRef.current.clear()

      let changedIds: string[] = []
      // The boundaries this call re-recorded into the seam memo — see the retire loop below.
      let recordedSeamUpstreamIds = new Set<string>()

      if (
        survivorFrames.length > 0 &&
        !chainStoryRedistributedRef.current.chainIds.has(deletedChain.id)
      ) {
        chainStoryRedistributedRef.current.chainIds.add(deletedChain.id)

        // No `tailOverflowsPastLastFrame`: the last survivor absorbs whatever is left, unclipped.
        // That is the overset model, and it is what a chain collapsing to a lone frame lands on —
        // over-capacity content there is clipped and badged, never discarded. Auto-grow was declined.
        // Assembled across the window INCLUDING the dying frames, so their boundaries are read from
        // the memo like any other; recorded back over the survivors, which is the shape the chain
        // now has.
        const slices = distributeChainStory(chainSeamMemoRef.current, windowFrames, survivorFrames)

        recordedSeamUpstreamIds = new Set(survivorFrames.slice(0, -1).map((f) => f.id))

        const changed = changedSlices(survivorFrames, slices)

        applyStorySlices(survivorFrames, changed, storyTxnId)
        changedIds = changed.map((slice) => slice.id)
      }

      // Retire every boundary of the pre-delete window this call did not just re-record. The dying
      // frames' own entries, obviously — but also a SURVIVOR's entry whose downstream neighbour died,
      // which the redistribution above would normally overwrite and does not when it is skipped (the
      // once-per-chain-per-gesture guard, or a chain collapsing below two members). Left behind, such
      // an entry is later validated against a frame it never described. Runs after the assembly,
      // which legitimately reads a dying frame's boundary.
      for (const f of windowFrames) {
        if (!recordedSeamUpstreamIds.has(f.id)) chainSeamMemoRef.current.delete(f.id)
      }

      // Advance the live mirror before the state write so the next frame's repair in this same
      // synchronous batch sees the reduced chain; the functional setter then makes the committed
      // state independent of how many repairs the batch queued and in what order.
      textChainsRef.current = chainsAfter
      setTextChains((prev) => removeBoxFromChains(prev, deletedUid))
      pushAction({
        type: 'textLinkChange',
        chainsBefore,
        chainsAfter,
        riderTextBoxId: chainRiderFrame?.id ?? bystanderRiderFrame?.id ?? deleted.id,
        historyTxnId,
      })

      const persistFrame = chainRiderFrame?.frame ?? bystanderRiderFrame

      if (!persistFrame) {
        // Every member of the chain went in this one gesture, so no frame survives to carry the
        // topology change. Returning here left the server holding a chain naming uids that no longer
        // exist, permanently: the backend does no reconciliation of its own, and `sanitizeChains`
        // runs on load without ever writing back — so the orphan was re-read and re-dropped locally
        // on every load, forever (WMPCM-7036 AC4).
        //
        // **The prune rides on the frame this call is deleting.** That looks perverse — why write to
        // something you are about to delete — but it is the only element guaranteed to exist, and
        // the API leaves no alternative:
        //
        // - The chain list is a spread-level sidecar accepted only riding on an element add or
        //   update (`checkpoint_controller.go` — `AddCheckpointElements` / `UpdateCheckpointElements`
        //   are the sole callers of `UpsertLinkedTextElements`).
        // - `DELETE .../elements/delete/{ids}` takes ids in the URL and no body, so the prune cannot
        //   ride on the delete itself.
        // - An element-less update is rejected: `UpdateCheckpointElements` runs
        //   `ValidateElementTotalCount(GreaterThan, 0)`, so `{ StowedElements: [] }` plus a chain
        //   list is a 422 — there is no "chain-only write".
        //
        // So the prune must ride on an element still live server-side, and an earlier attempt to use
        // an unrelated bystander ("carrier") for that was wrong twice over: it found nothing at all
        // on a spread holding only the chain (reproducing the very AC4 orphan it was added for), and
        // where it did find one it pushed a full element replacement — and a spurious multiplayer
        // broadcast — for a shape the user never touched, dropping `visible*` off a rotated one.
        //
        // The dying frame has neither problem. `handleShapeDeleted` calls this repair *before* its
        // `enqueueDelete`, so the element is still live at this moment; `ChangeQueue.enqueue`'s
        // send-ahead exception (see its rider comment) then puts this update on the wire before the
        // delete that supersedes it. If that ordering ever loses the race the update 404s, the
        // controller returns before `UpsertLinkedTextElements`, and the orphan simply survives — the
        // old behaviour, not a new corruption.
        //
        // Deliberately no `markEdited`: it acquires an element lock, and this element is on its way
        // out. `riderTextBoxId` above already falls through to `deleted.id`, so undo/redo rides the
        // same element this does.
        enqueueUpdate(
          buildStowedShapeElement(
            shapeTextBakeRef.current,
            deleted.shape,
            undefined,
            spreadProjection,
            fonts
          ),
          {
            operation: 'update',
            // `deleted.shape` first: it may already be gone from `smgr` by this point (see the
            // param doc above), and any surviving chain this payload also describes still needs its
            // members' uids resolved against the live manager. See `toWireTextChains`.
            linkedTextElements: {
              textChain: toWireTextChains(chainsAfter, [
                deleted.shape,
                ...(smgr?.getShapes() ?? []),
              ]),
            },
            meta: historyTxnId ? { historyTxnId } : undefined,
          }
        )

        return
      }

      if (!isShapeBackedFrame(persistFrame)) return

      const editedIds = changedIds.length > 0 ? changedIds : [persistFrame.id]

      void markEdited(editedIds)
      persistChainRider(chainsAfter, persistFrame.id, storyTxnId, new Set(changedIds))
    },
    // `textChains` is deliberately absent: chain state is read through `textChainsRef` so a
    // multi-frame gesture sees its own prior repairs, and re-creating this callback per chain
    // change would serve no purpose.
    [
      applyStorySlices,
      buildChainStoryFrames,
      enqueueUpdate,
      markEdited,
      persistChainRider,
      pushAction,
      spreadProjection,
      chainProjectionBaselineRef,
      chainSeamMemoRef,
      chainStoryRedistributedRef,
      deleteGestureScopeRef,
      fontLookupRef,
      setTextChains,
      shapeManagerRef,
      shapeTextBakeRef,
      textChainsRef,
    ]
  )

  /**
   * WMPCM-6924: apply a whole-box style change across every frame of the chain(s) the current
   * selection touches, as one atomic (single-undo) operation. A selected frame that isn't in any
   * chain is styled on its own ("chain of one") so a mixed selection still updates uniformly.
   * Metric-changing styles (font size, leading) then reflow each chain once so the flow stays
   * fitted — every mutation shares one historyTxnId, so a single Ctrl+Z reverts the whole thing.
   * Spans both managers (WMPCM-7988/9070): the seed selection, the uid→object resolution, and the
   * write-back all go through `collectLinkableFrames`/`isShapeBackedFrame`, so a shape acting as a
   * text frame is styled like any other member.
   *
   * Returns `handled: true` when the selection touched a chain and was handled here; `false` lets the
   * caller fall back to the normal per-object apply. `claimedShapeIds` names the shapes styled here so
   * the caller can keep `applyShapeTextRunStyle` from styling them a second time. Only reachable from
   * the Select-tool branch of handleTextStyleUpdate — inline editing keeps its own selection-range
   * apply (AC4).
   */
  const applyTextStyleAcrossChain = useCallback(
    (update: TextRunStyleUpdate): ChainStyleApplyResult => {
      const smgr = shapeManagerRef.current

      // Resolved through `collectLinkableFrames`, so a shape acting as a text frame is a chain
      // member here like anywhere else (WMPCM-7988).
      const allShapes = smgr?.getShapes() ?? []
      const allFrames = collectLinkableFrames(allShapes)
      const frameById = new Map(allFrames.map((f) => [f.id, f] as const))
      const frameIdByUid = new Map<string, string>()

      for (const f of allFrames) {
        if (f.uid != null) frameIdByUid.set(String(f.uid), f.id)
      }

      const selectedShapeIds = smgr?.getSelectedShapeIds() ?? []
      const seedFrameIds = selectedShapeIds.filter((id) => frameById.has(id))

      if (seedFrameIds.length === 0) return NOT_HANDLED_CHAIN_STYLE

      const seedUids = seedFrameIds
        .map((id) => frameById.get(id))
        .filter((f): f is LinkableTextFrame => f != null && f.uid != null)
        .map((f) => String(f.uid))

      const chains = chainsContainingAny(textChains, seedUids)

      // Not touching any chain — let the caller fall back to the normal per-object apply.
      if (chains.length === 0) return NOT_HANDLED_CHAIN_STYLE

      const chainMemberUids = new Set(chains.flatMap((c) => c.textBox.map((tb) => tb.uid)))

      // Full target set: every member of every touched chain, plus any selected frame not in a chain.
      const targetIds = new Set<string>()

      for (const chain of chains) {
        for (const tb of chain.textBox) {
          const id = frameIdByUid.get(tb.uid)

          if (id) targetIds.add(id)
        }
      }

      for (const id of seedFrameIds) {
        const f = frameById.get(id)

        if (f && (f.uid == null || !chainMemberUids.has(String(f.uid)))) targetIds.add(id)
      }

      const historyTxnId = `textlink-style:${crypto.randomUUID()}`
      const touchedIds: string[] = []
      const claimedShapeIds = new Set<string>()

      // Phase 1 — apply the style delta whole-frame to every target + record undo history. Chain
      // members are persisted by the Phase 2 reflow below (which builds from post-style manager
      // state); standalone frames aren't part of a reflow, so they enqueue here.
      for (const id of targetIds) {
        const frame = frameById.get(id)

        // `locked` only — a style apply writes `paragraphs`, never geometry, so a frozen member
        // restyles with the rest of its chain (WMPCM-6980). See `canEditChainFrameContent`.
        if (!frame || !canEditChainFrameContent(frame)) continue

        const before = frame.paragraphs
        const after = applyStyleUpdateToParagraphs(before, update)
        const isChainMember = frame.uid != null && chainMemberUids.has(String(frame.uid))

        if (isShapeBackedFrame(frame)) {
          const shapeParagraphs = paragraphsForWriteBack(after)

          claimedShapeIds.add(id)
          // Same class of write, same reason, as the reflow write-back above: a locked or frozen
          // chain member still receives flowed text (WMPCM-6972 / WMPCM-7052 AC3), and
          // `shouldBlockShapeUpdate` would otherwise reject it outright and drop the text.
          smgr?.updateShape(id, { paragraphs: shapeParagraphs }, { bypassEditGuards: true })
          pushAction({
            type: 'update',
            shapeId: id,
            before: { paragraphs: before },
            after: { paragraphs: shapeParagraphs },
            historyTxnId,
          })

          if (!isChainMember) {
            enqueueUpdate(
              buildStowedShapeElement(
                shapeTextBakeRef.current,
                { ...frame.shape, paragraphs: shapeParagraphs },
                undefined,
                spreadProjection,
                fontLookupRef.current ?? undefined
              ),
              { operation: 'update', meta: { historyTxnId } }
            )
          }

          touchedIds.push(id)
        }
      }

      // Every target frame was locked — nothing to do, but the selection did touch a chain, so the
      // caller must not fall back to its per-object apply (which would hit the same locked frames).
      if (touchedIds.length === 0) return { handled: true, claimedShapeIds }

      void markEdited(touchedIds)

      // Phase 2 — redistribute each touched chain's story (font size / leading change line-fit);
      // shares the same historyTxnId so it stays one undo step. Force-persist the touched members so
      // a style-only change that moves no content still saves on every frame.
      //
      // D7: this needed no new behaviour. `chainsContainingAny` above already targets every member of
      // every touched chain, so the apply is "style the story once, then distribute". D4 makes the
      // distribution symmetric — shrinking the type now reclaims from downstream rather than leaving
      // the freed room empty, which is the reversal of the old reflow's `allowBackwardPull` default.
      const touchedSet = new Set(touchedIds)

      for (const chain of chains) {
        const headUid = chain.textBox[0]?.uid
        const headId = headUid ? frameIdByUid.get(headUid) : undefined

        if (!headUid || !headId) continue

        const chainForceIds = new Set(
          chain.textBox
            .map((tb) => frameIdByUid.get(tb.uid))
            .filter((x): x is string => x != null && touchedSet.has(x))
        )

        redistributeChainStory(textChains, headUid, headId, historyTxnId, {
          forcePersistFrameIds: chainForceIds,
        })
      }

      bumpTextPanelRevision((n) => n + 1)

      return { handled: true, claimedShapeIds }
    },
    [
      textChains,
      enqueueUpdate,
      pushAction,
      markEdited,
      redistributeChainStory,
      spreadProjection,
      bumpTextPanelRevision,
      fontLookupRef,
      shapeManagerRef,
      shapeTextBakeRef,
    ]
  )

  // handleTextStyleUpdate (in useHomeFoundation) reaches this chain-aware apply through a ref so it
  // can stay a stable `[]`-dep callback and still call the latest closure (which tracks textChains).
  applyTextStyleAcrossChainRef.current = applyTextStyleAcrossChain

  return {
    nextTextFrameUidRef,
    mintedTextFrameUidHighWaterRef,
    mintTextFrameUid,
    reserveTextFrameUidsThrough,
    buildChainStoryFrames,
    applyStorySlices,
    persistChainRider,
    projectChainStoryLive,
    projectAllChainsLive,
    redistributeChainStory,
    repairChainsAfterHistoryStep,
    repairChainAfterFrameDeleted,
    applyTextStyleAcrossChain,
  }
}
